import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import PageBar from '../../components/PageBar';
import Icon from '../../components/common/Icon';
import LoadingDots from '../../components/common/LoadingDots';
import ToggleSwitch from '../../components/common/ToggleSwitch';
import { sendContactMessage } from '../../services/contactService';
import theme from '../../utils/theme';
import { getColorTheme } from '../../utils/colorTheme';
import { scrollTop, setDocTitle } from '../../utils/utilities';
import packageJson from '../../../package.json';
import { ReactComponent as BugIcon } from '../../icons/warning-triangle.svg';
import { ReactComponent as IdeaIcon } from '../../icons/light-bulb.svg';
import { ReactComponent as ChatIcon } from '../../icons/chat-lines.svg';
import { ReactComponent as CheckIcon } from '../../icons/check.svg';
import { ReactComponent as CheckCircleIcon } from '../../icons/check-circle-solid.svg';
import './Contact.scss';

const MESSAGE_MAX = 5000;

// Worded under `contact.categories.<id>`: title, hint and placeholder
const CATEGORIES = [
  { id: 'bug', icon: <BugIcon /> },
  { id: 'feature', icon: <IdeaIcon /> },
  { id: 'general', icon: <ChatIcon /> },
];

// Device info goes with bug reports only, and only when the user switches it on
const emptyForm = { category: null, message: '', includeDeviceInfo: false };

function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setDocTitle(t('settings.help.contactUs'));
  }, [t]);

  useEffect(() => {
    // Opened from the bottom of Settings, which leaves the window scrolled down
    scrollTop(undefined, false);
  }, []);

  const category = CATEGORIES.find((c) => c.id === form.category);
  const canSend = category && form.message.trim() && form.message.length <= MESSAGE_MAX;
  const isBug = form.category === 'bug';

  const handleChange = ({ currentTarget }) => setForm({ ...form, [currentTarget.name]: currentTarget.value });

  const buildDiagnostics = () => ({
    appVersion: packageJson.version,
    userAgent: navigator.userAgent.slice(0, 300),
    screen: `${window.innerWidth}×${window.innerHeight}`,
    theme: theme.isDarkTheme() ? 'dark' : 'light',
    colorTheme: getColorTheme(),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSend || sending) return;

    setSending(true);
    try {
      await sendContactMessage({
        category: form.category,
        message: form.message.trim(),
        ...(isBug && form.includeDeviceInfo && { diagnostics: buildDiagnostics() }),
      });
      setSent(true);
    } catch (ex) {
      const status = ex.response?.status;
      if (status === 429) toast.error(t('contact.tooMany'));
      else if (status === 503) toast.error(t('contact.unavailable'));
      else if (status === 400) toast.error(t('contact.checkMessage'));
      // Network errors and 5xx are already toasted by httpService; any other 4xx would pass silently
      else if (status < 500) toast.error(t('contact.notSent'));
    } finally {
      setSending(false);
    }
  };

  const handleSendAnother = () => {
    setForm(emptyForm);
    setSent(false);
  };

  return (
    <div className="mg-container">
      <PageBar back={{ label: t('common.back'), icon: true }} title={t('settings.help.contactUs')} />
      <div className="Contact">
        {sent ? (
          <div className="section sent" role="status">
            <Icon icon={<CheckCircleIcon />} className="solid sent-icon" />
            <h2 className="sent-title">{t('contact.sentTitle')}</h2>
            <p className="sent-text">{t('contact.sentText')}</p>
            <div className="sent-actions">
              <button type="button" className="mg-btn mg-btn--light" onClick={handleSendAnother}>
                {t('contact.sendAnother')}
              </button>
              <Link to="/songs" className="mg-btn mg-btn--primary">
                {t('contact.backToSongs')}
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="section">
              <h6 className="section-title" id="contact-category-title">
                {t('contact.about')}
              </h6>
              <p className="intro">{t('contact.intro')}</p>
              <div className="categories" role="radiogroup" aria-labelledby="contact-category-title">
                {CATEGORIES.map((c) => (
                  <label key={c.id} className={cx('category', { 'is-selected': form.category === c.id })}>
                    <input
                      type="radio"
                      name="category"
                      value={c.id}
                      checked={form.category === c.id}
                      onChange={handleChange}
                      className="mg-sr-only"
                    />
                    <span className="category-icon" aria-hidden="true">
                      <Icon icon={c.icon} />
                    </span>
                    <span className="category-text">
                      <span className="category-title">{t(`contact.categories.${c.id}.title`)}</span>
                      <span className="category-hint">{t(`contact.categories.${c.id}.hint`)}</span>
                    </span>
                    <span className="category-check" aria-hidden="true">
                      <CheckIcon />
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="section">
              <h6 className="section-title">{t('contact.yourMessage')}</h6>
              <div className="fields">
                <label htmlFor="message" className="mg-sr-only">
                  {t('contact.message')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  className="mg-form-control message"
                  rows={7}
                  maxLength={MESSAGE_MAX}
                  placeholder={category ? t(`contact.categories.${category.id}.placeholder`) : t('contact.pickTopic')}
                  value={form.message}
                  onChange={handleChange}
                />
                <div className="counter">
                  {form.message.length} / {MESSAGE_MAX}
                </div>
              </div>
              {isBug && (
                <div className="device-info">
                  <ToggleSwitch
                    name="includeDeviceInfo"
                    checked={form.includeDeviceInfo}
                    label={t('contact.deviceInfo')}
                    description={t('contact.deviceInfoHint')}
                    onChange={() => setForm({ ...form, includeDeviceInfo: !form.includeDeviceInfo })}
                    showOnOffText={true}
                  />
                </div>
              )}
            </div>

            <p className="note">{t('contact.replyNote')}</p>

            <button
              type="submit"
              className="mg-btn mg-btn--primary mg-btn--block submit"
              disabled={!canSend || sending}
            >
              {sending ? <LoadingDots width="1.5em" /> : t('contact.send')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default Contact;
